'use client';

import React, { useEffect, useRef } from 'react';

/* ── Decorative SVG: Capital — stacked coins / layered rings ── */
const VisualCapital = () => (
  <svg width="64" height="64" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <ellipse cx="36" cy="52" rx="22" ry="8" stroke="var(--color-primary)" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.3" />
    <ellipse cx="36" cy="44" rx="22" ry="8" fill="rgba(27,67,50,0.06)" stroke="var(--color-primary)" strokeWidth="1.5" opacity="0.5" />
    <ellipse cx="36" cy="36" rx="22" ry="8" fill="rgba(27,67,50,0.1)" stroke="var(--color-primary)" strokeWidth="1.5" opacity="0.7" />
    <ellipse cx="36" cy="28" rx="22" ry="8" fill="rgba(82,183,136,0.15)" stroke="var(--color-accent)" strokeWidth="1.8" />
    <text x="36" y="32" textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--color-primary)" fontFamily="DM Sans, sans-serif" letterSpacing="0.05em">CAPITAL</text>
    <line x1="14" y1="28" x2="14" y2="44" stroke="var(--color-primary)" strokeWidth="1.5" opacity="0.4" />
    <line x1="58" y1="28" x2="58" y2="44" stroke="var(--color-primary)" strokeWidth="1.5" opacity="0.4" />
  </svg>
);

const CLOCK_TICKS = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];
const CLOCK_TICK_LINES = CLOCK_TICKS?.map((deg, i) => {
  const rad = (deg - 90) * Math.PI / 180;
  const r1 = i % 3 === 0 ? 17 : 18.5;
  const r2 = 20;
  const round = (n: number) => Math.round(n * 10000) / 10000;
  return {
    key: deg,
    x1: round(36 + r1 * Math.cos(rad)),
    y1: round(36 + r1 * Math.sin(rad)),
    x2: round(36 + r2 * Math.cos(rad)),
    y2: round(36 + r2 * Math.sin(rad)),
    strokeWidth: i % 3 === 0 ? 2 : 1,
  };
});

const VisualSpeed = () => (
  <svg width="64" height="64" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="36" cy="36" r="26" stroke="rgba(116,198,157,0.4)" strokeWidth="1.5" />
    <circle cx="36" cy="36" r="20" stroke="rgba(116,198,157,0.6)" strokeWidth="1.5" />
    {CLOCK_TICK_LINES?.map((tick) => (
      <line key={tick?.key} x1={tick?.x1} y1={tick?.y1} x2={tick?.x2} y2={tick?.y2} stroke="rgba(116,198,157,0.7)" strokeWidth={tick?.strokeWidth} />
    ))}
    <line x1="36" y1="36" x2="26" y2="22" stroke="#74C69D" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="36" y1="36" x2="46" y2="24" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
    <circle cx="36" cy="36" r="2.5" fill="#74C69D" />
    <path d="M40 14 L35 24 L39 24 L34 34" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const VisualOwnership = () => (
  <svg width="64" height="64" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="36" cy="36" r="30" stroke="rgba(27,67,50,0.08)" strokeWidth="1" />
    <path d="M36 12 L56 20 V36 C56 48 46 56 36 60 C26 56 16 48 16 36 V20 Z" fill="rgba(27,67,50,0.07)" stroke="var(--color-primary)" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M36 18 L50 24.5 V36 C50 45 43 51 36 54 C29 51 22 45 22 36 V24.5 Z" fill="rgba(82,183,136,0.12)" stroke="var(--color-accent)" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M28 36 L33 41 L44 30" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const pillars = [
  {
    number: '01',
    badge: 'Capital Preservation',
    Visual: VisualCapital,
    headline: 'Deploy capital across your entire pipeline.',
    body: "Secure multiple assets simultaneously without freezing your corporate liquidity. We provide the short-term capital so you don't have to bring in dilutive early-stage partners.",
    stat: '25×',
    statLabel: 'More Buying Power',
    proof: ['Fund multiple deals simultaneously', 'Eliminate capital lockup during option periods', 'Scale without tying up your cash commitment'],
    dark: false,
  },
  {
    number: '02',
    badge: 'Speed & Reliability',
    Visual: VisualSpeed,
    headline: 'Move faster than the competition.',
    body: 'In fast-moving markets, timing is everything. Our streamlined underwriting ensures quick decisions and a reliable wire that hits escrow exactly when you need it.',
    stat: '72hr',
    statLabel: 'Average funding time',
    proof: ['1-page application, same-day review', 'Silent partner — zero seller or broker friction', 'Clear requirements, transparent decisions'],
    dark: true,
  },
  {
    number: '03',
    badge: 'Deal Ownership',
    Visual: VisualOwnership,
    headline: 'Lock the deal. Control the terms.',
    body: 'Bringing in a heavy equity partner just to cover the deposit strips away your negotiating leverage. Earnest Bridge lets you secure the contract first, so you can structure your capital stack without pressure.',
    stat: '100%',
    statLabel: 'Negotiating leverage retained',
    proof: ['No equity dilution to cover the deposit', 'Test capital markets freely after lock-up', 'No one holds your deal hostage'],
    dark: false,
  },
];

export default function PillarsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -4% 0px' }
    );
    const els = sectionRef?.current?.querySelectorAll('.animate-on-scroll');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="pillars"
      className="relative z-10 pt-24 pb-0 overflow-hidden"
      style={{ background: 'var(--color-bg)' }}
      aria-label="Three reasons deal-makers choose EarnestBridge"
    >
      {/* Subtle background texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(27,67,50,0.04) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      {/* ── Section header — stays in primary page grid ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div
          className="animate-on-scroll mb-16 max-w-2xl"
          style={{
            animationName: 'animationIn',
            animationDuration: '0.9s',
            animationTimingFunction: 'cubic-bezier(0.16,1,0.3,1)',
            animationFillMode: 'forwards',
            opacity: 0,
          }}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-5 h-px" style={{ background: 'rgba(201,168,76,0.5)' }} />
            <p className="section-label">Why EarnestBridge</p>
          </div>
          <h2
            className="font-display"
            style={{
              fontSize: 'clamp(2.25rem, 5vw, 4rem)',
              color: 'var(--color-foreground)',
              letterSpacing: '-0.025em',
              lineHeight: 1.05,
            }}
          >
            Three reasons deal-makers
            <br />
            <span className="italic" style={{ color: 'var(--color-primary)' }}>
              choose us over alternatives.
            </span>
          </h2>
        </div>
      </div>

      {/* ── Pillar rows — full-width editorial grid on desktop ── */}
      {/* On desktop: rows span the full usable width with px-4 sm:px-6 lg:px-8 gutters */}
      {/* On mobile: contained within max-w-7xl */}
      <div className="relative">
        {/* Top border of the grid — centered to match 82% grid on desktop */}
        <div className="hidden lg:flex justify-center">
          <div
            className="h-px"
            style={{ width: '82%', maxWidth: '1400px', background: 'rgba(27,67,50,0.12)' }}
          />
        </div>
        {/* Mobile top border — contained */}
        <div
          className="lg:hidden max-w-7xl mx-auto px-4 sm:px-6"
          style={{ borderTop: '1px solid rgba(27,67,50,0.12)' }}
        />

        {pillars?.map((pillar, idx) => {
          const { Visual } = pillar;
          return (
            <div
              key={pillar?.number}
              className="animate-on-scroll w-full relative"
              style={{
                animationName: 'animationIn',
                animationDuration: '0.9s',
                animationDelay: `${0.1 + idx * 0.12}s`,
                animationTimingFunction: 'cubic-bezier(0.16,1,0.3,1)',
                animationFillMode: 'forwards',
                opacity: 0,
                background: pillar?.dark
                  ? '#0D1F17'
                  : idx === 2
                  ? 'var(--color-surface)'
                  : 'var(--color-bg)',
              }}
            >
              {/* ── Mobile layout (unchanged) ── */}
              <div className="lg:hidden max-w-7xl mx-auto px-4 sm:px-6">
                <div
                  className="flex flex-col gap-5 py-6"
                  style={{
                    borderBottom: pillar?.dark
                      ? '1px solid rgba(116,198,157,0.12)'
                      : '1px solid rgba(27,67,50,0.09)',
                  }}
                >
                  {/* Top row: number+badge left, stat right */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-col">
                      <span
                        className="font-display font-bold leading-none select-none"
                        style={{
                          fontSize: '3rem',
                          color: pillar?.dark
                            ? 'rgba(255,255,255,0.08)'
                            : 'rgba(27,67,50,0.09)',
                          letterSpacing: '-0.04em',
                        }}
                      >
                        {pillar?.number}
                      </span>
                      <span
                        className="inline-block mt-2 text-[9px] font-bold uppercase tracking-[0.22em]"
                        style={{
                          color: pillar?.dark ? 'rgba(201,168,76,0.75)' : 'var(--color-primary)',
                          borderBottom: pillar?.dark
                            ? '1px solid rgba(201,168,76,0.25)'
                            : '1px solid rgba(27,67,50,0.2)',
                          paddingBottom: '3px',
                        }}
                      >
                        {pillar?.badge}
                      </span>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p
                        className="font-display font-bold leading-none"
                        style={{
                          fontSize: 'clamp(2.5rem, 10vw, 3.5rem)',
                          color: pillar?.dark ? '#C9A84C' : 'var(--color-primary)',
                          letterSpacing: '-0.03em',
                        }}
                      >
                        {pillar?.stat}
                      </p>
                      <p
                        className="text-[10px] font-semibold uppercase tracking-[0.18em] mt-1 max-w-[16ch] ml-auto"
                        style={{
                          color: pillar?.dark ? 'rgba(240,237,230,0.55)' : 'var(--color-muted)',
                          lineHeight: 1.4,
                        }}
                      >
                        {pillar?.statLabel}
                      </p>
                    </div>
                  </div>

                  {/* Divider */}
                  <div
                    className="w-full h-px"
                    style={{
                      background: pillar?.dark
                        ? 'rgba(255,255,255,0.08)'
                        : 'rgba(27,67,50,0.08)',
                    }}
                  />

                  {/* Headline + body */}
                  <div>
                    <h3
                      className="font-display font-semibold leading-tight mb-3"
                      style={{
                        fontSize: 'clamp(1.15rem, 4.5vw, 1.4rem)',
                        color: pillar?.dark ? '#F0EDE6' : 'var(--color-foreground)',
                        letterSpacing: '-0.015em',
                      }}
                    >
                      {pillar?.headline}
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{
                        color: pillar?.dark ? 'rgba(240,237,230,0.60)' : 'var(--color-muted)',
                      }}
                    >
                      {pillar?.body}
                    </p>
                  </div>

                  {/* Proof points */}
                  <ul className="flex flex-col gap-2">
                    {pillar?.proof?.map((point) => (
                      <li key={point} className="flex items-start gap-2.5">
                        <span
                          className="mt-1.5 flex-shrink-0"
                          style={{
                            width: '4px',
                            height: '4px',
                            borderRadius: '50%',
                            background: pillar?.dark ? 'rgba(201,168,76,0.55)' : 'rgba(27,67,50,0.4)',
                            display: 'block',
                          }}
                        />
                        <span
                          className="text-sm leading-snug"
                          style={{
                            color: pillar?.dark ? 'rgba(240,237,230,0.75)' : 'var(--color-muted)',
                          }}
                        >
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Gold accent tag — only on dark card */}
                  {pillar?.dark && (
                    <div className="self-start">
                      <div
                        className="text-[9px] font-bold uppercase tracking-[0.2em]"
                        style={{
                          color: 'rgba(201,168,76,0.75)',
                          borderBottom: '1px solid rgba(201,168,76,0.25)',
                          paddingBottom: '3px',
                        }}
                      >
                        Silent Funding Partner
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Desktop layout — widened editorial three-column grid (~82% viewport) ── */}
              <div
                className="hidden lg:flex items-stretch mx-auto"
                style={{
                  width: '82%',
                  maxWidth: '1400px',
                  borderBottom: pillar?.dark
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid rgba(27,67,50,0.09)',
                }}
              >
                {/* ── Col 1: Number + Badge + Visual ── */}
                <div
                  className="flex flex-col justify-between py-10 xl:py-12 min-w-0 flex-shrink-0"
                  style={{
                    width: '22%',
                    paddingRight: '3rem',
                    borderRight: pillar?.dark
                      ? '1px solid rgba(255,255,255,0.07)'
                      : '1px solid rgba(27,67,50,0.07)',
                  }}
                >
                  <div>
                    <span
                      className="font-display font-bold block leading-none select-none"
                      style={{
                        fontSize: 'clamp(4.5rem, 7vw, 7rem)',
                        color: pillar?.dark
                          ? 'rgba(255,255,255,0.07)'
                          : 'rgba(27,67,50,0.08)',
                        letterSpacing: '-0.04em',
                      }}
                    >
                      {pillar?.number}
                    </span>
                    <span
                      className="inline-block mt-4 text-[9px] font-bold uppercase tracking-[0.22em]"
                      style={{
                        color: pillar?.dark ? 'rgba(201,168,76,0.75)' : 'var(--color-primary)',
                        borderBottom: pillar?.dark
                          ? '1px solid rgba(201,168,76,0.25)'
                          : '1px solid rgba(27,67,50,0.2)',
                        paddingBottom: '3px',
                      }}
                    >
                      {pillar?.badge}
                    </span>
                  </div>
                  <div className="mt-8 opacity-70">
                    <Visual />
                  </div>
                </div>

                {/* ── Col 2: Headline + Body + Proof ── */}
                <div
                  className="flex flex-col justify-center py-10 xl:py-12 min-w-0 flex-1"
                  style={{
                    paddingLeft: '3rem',
                    paddingRight: '3rem',
                    borderRight: pillar?.dark
                      ? '1px solid rgba(255,255,255,0.07)'
                      : '1px solid rgba(27,67,50,0.07)',
                  }}
                >
                  <h3
                    className="font-display font-semibold leading-tight mb-5"
                    style={{
                      fontSize: 'clamp(1.4rem, 2.2vw, 2.1rem)',
                      color: pillar?.dark ? '#F0EDE6' : 'var(--color-foreground)',
                      letterSpacing: '-0.015em',
                    }}
                  >
                    {pillar?.headline}
                  </h3>
                  <p
                    className="leading-relaxed mb-8"
                    style={{
                      fontSize: 'clamp(0.875rem, 1vw, 1rem)',
                      color: pillar?.dark ? 'rgba(240,237,230,0.60)' : 'var(--color-muted)',
                      maxWidth: '52ch',
                    }}
                  >
                    {pillar?.body}
                  </p>
                  <ul className="flex flex-col gap-3">
                    {pillar?.proof?.map((point) => (
                      <li key={point} className="flex items-start gap-3">
                        <span
                          className="mt-1.5 flex-shrink-0"
                          style={{
                            width: '4px',
                            height: '4px',
                            borderRadius: '50%',
                            background: pillar?.dark ? 'rgba(201,168,76,0.55)' : 'rgba(27,67,50,0.4)',
                            display: 'block',
                          }}
                        />
                        <span
                          className="text-sm leading-snug"
                          style={{
                            color: pillar?.dark ? 'rgba(240,237,230,0.75)' : 'var(--color-muted)',
                          }}
                        >
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* ── Col 3: Large Metric ── */}
                <div
                  className="flex flex-col justify-center items-center py-10 xl:py-12 flex-shrink-0"
                  style={{
                    width: '28%',
                    paddingLeft: '3rem',
                  }}
                >
                  <div className="text-center">
                    <p
                      className="font-display font-bold leading-none"
                      style={{
                        fontSize: 'clamp(3.5rem, 5.5vw, 6.5rem)',
                        color: pillar?.dark ? '#C9A84C' : 'var(--color-primary)',
                        letterSpacing: '-0.03em',
                      }}
                    >
                      {pillar?.stat}
                    </p>
                    <p
                      className="font-semibold uppercase mt-3"
                      style={{
                        fontSize: '11px',
                        letterSpacing: '0.2em',
                        color: pillar?.dark ? 'rgba(240,237,230,0.55)' : 'var(--color-muted)',
                        lineHeight: 1.5,
                        maxWidth: '18ch',
                      }}
                    >
                      {pillar?.statLabel}
                    </p>
                  </div>
                  <div
                    className="mt-8 w-10 h-px"
                    style={{
                      background: pillar?.dark
                        ? 'rgba(201,168,76,0.30)'
                        : 'rgba(27,67,50,0.15)',
                    }}
                  />
                  {pillar?.dark && (
                    <div
                      className="mt-6 text-[9px] font-bold uppercase tracking-[0.2em]"
                      style={{
                        color: 'rgba(201,168,76,0.75)',
                        borderBottom: '1px solid rgba(201,168,76,0.25)',
                        paddingBottom: '3px',
                      }}
                    >
                      Silent Funding Partner
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
